import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Crown, Shield, Sword, Eye, Ghost, Star, DollarSign, Hash, MapPin, Target, Flame, BookOpen, ChevronLeft } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

// Role visual data with full goals
const ROLE_VISUALS = {
  reaper: { name: 'THE REAPER', faction: 'CMD', color: '#C9A84C', icon: <Sword size={16} />, goal: 'Control most territory at end', night: 'Investigate a player or protect yourself' },
  consigliere: { name: 'THE CONSIGLIERE', faction: 'CMD', color: '#8B6914', icon: <Shield size={16} />, goal: 'Protect the Reaper. If Reaper survives, you win', night: 'Shield one player from elimination' },
  ghost: { name: 'THE GHOST', faction: 'Neutral', color: '#9B59B6', icon: <Ghost size={16} />, goal: 'Survive to the end and profit', night: 'Hide — cannot be targeted' },
  badge: { name: 'THE BADGE', faction: 'Law', color: '#3498DB', icon: <Eye size={16} />, goal: 'Expose 2 CMD players during day votes', night: 'Investigate a player\'s true role' },
  connect: { name: 'THE CONNECT', faction: 'Neutral', color: '#27AE60', icon: <Users size={16} />, goal: 'Back the winning faction', night: 'Observe — learn who visited whom' },
  rat: { name: 'THE RAT', faction: 'Hidden', color: '#E74C3C', icon: <Target size={16} />, goal: 'Destroy CMD from inside — vote out CMD members', night: 'Sabotage — disrupt a player\'s night action' },
  boss: { name: 'THE BOSS', faction: 'CMD', color: '#FFD700', icon: <Crown size={16} />, goal: 'Win the war — eliminate all non-CMD', night: 'Eliminate one player' },
  earner: { name: 'THE EARNER', faction: 'CMD', color: '#4ADE80', icon: <DollarSign size={16} />, goal: 'Generate the most income during the game', night: 'Earn — gain $5,000 per round' },
};

// =============================================
// WATERFRONT TABLE GAME
// =============================================
const WaterfrontTable = ({ gameId, onBack }) => {
  const [view, setView] = useState('lobby'); // lobby, howto, game
  const [openTables, setOpenTables] = useState([]);
  const [tableId, setTableId] = useState(null);
  const [tableState, setTableState] = useState(null);
  const [maxPlayers, setMaxPlayers] = useState(4);
  const [loading, setLoading] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [msg, setMsg] = useState(null);

  const loadTables = useCallback(async () => {
    const res = await fetch(`${API}/api/table/open`);
    if (res.ok) setOpenTables((await res.json()).tables || []);
  }, []);

  const loadStatus = useCallback(async () => {
    if (!tableId) return;
    const res = await fetch(`${API}/api/table/status/${tableId}?game_id=${gameId}`);
    if (res.ok) { setTableState(await res.json()); setSelectedTarget(null); }
  }, [tableId, gameId]);

  useEffect(() => { loadTables(); }, [loadTables]);
  useEffect(() => { if (tableId) { loadStatus(); const i = setInterval(loadStatus, 3000); return () => clearInterval(i); } }, [tableId, loadStatus]);

  const createTable = async () => {
    setLoading(true);
    const res = await fetch(`${API}/api/table/create`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ game_id: gameId, max_players: maxPlayers }),
    });
    const d = await res.json();
    if (res.ok && d.success) { setTableId(d.table_id); setView('game'); }
    else setMsg(d.detail);
    setLoading(false);
  };

  const joinTable = async (tid) => {
    const res = await fetch(`${API}/api/table/join`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ game_id: gameId, table_id: tid }),
    });
    const d = await res.json();
    if (res.ok && d.success) { setTableId(tid); setView('game'); }
    else setMsg(d.detail);
  };

  const startGame = async () => {
    const res = await fetch(`${API}/api/table/start/${tableId}`, { method: 'POST' });
    if (res.ok) loadStatus();
  };

  const submitNightAction = async (action) => {
    const res = await fetch(`${API}/api/table/night-action`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ game_id: gameId, table_id: tableId, action, target_game_id: selectedTarget }),
    });
    const d = await res.json();
    if (res.ok) { setMsg(d.narrator); setSelectedTarget(null); loadStatus(); }
    else setMsg(d.detail);
  };

  const submitVote = async (targetId) => {
    const res = await fetch(`${API}/api/table/vote`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ game_id: gameId, table_id: tableId, target_game_id: targetId }),
    });
    const d = await res.json();
    if (res.ok) { setMsg(d.narrator); loadStatus(); }
    else setMsg(d.detail);
  };

  // How-to-play view
  if (view === 'howto') {
    return (
      <div className="space-y-4" data-testid="table-howto">
        <button onClick={() => setView('lobby')} className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px] tracking-widest hover:text-[#C9A84C]">BACK TO LOBBY</button>
        <div className="text-center">
          <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#C9A84C]">HOW TO PLAY</h3>
          <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px] mt-1">THE WATERFRONT TABLE — SOCIAL DEDUCTION</p>
        </div>

        <div className="space-y-3">
          {[
            { num: '1', title: 'SIT DOWN', desc: 'Pay $5,000 to join. 2-8 players. Each gets a hidden role with a secret goal.' },
            { num: '2', title: 'NIGHT FALLS', desc: 'Each role has a night action. The Boss eliminates, the Consigliere protects, the Badge investigates. Choose your target carefully.' },
            { num: '3', title: 'DAY BREAKS', desc: 'Discuss and vote. The player with the most votes is eliminated. Exposed roles are revealed.' },
            { num: '4', title: 'REPEAT', desc: '5 rounds of Night/Day. Alliances form and break. Trust no one.' },
            { num: '5', title: 'ENDGAME', desc: 'CMD wins if Reaper survives. Law wins if Badge exposes 2 CMD. Neutrals win by surviving or backing the winner.' },
          ].map(s => (
            <div key={s.num} className="flex gap-3 p-3 bg-[#111820] border border-[#1A2535] rounded-lg">
              <span className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#C9A84C]/30">{s.num}</span>
              <div>
                <p className="font-['Outfit'] text-[#E8E0D0] text-xs font-bold">{s.title}</p>
                <p className="font-['Outfit'] text-[#E8E0D0]/40 text-[10px] mt-0.5">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div>
          <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[8px] tracking-widest mb-2">ALL ROLES</p>
          <div className="space-y-1.5">
            {Object.entries(ROLE_VISUALS).map(([k, v]) => (
              <div key={k} className="flex items-center gap-3 p-2.5 bg-[#0a0e12] border border-[#1A2535] rounded-lg">
                <span style={{ color: v.color }}>{v.icon}</span>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-['Outfit'] text-[10px] font-bold" style={{ color: v.color }}>{v.name}</span>
                    <span className="font-['Outfit'] text-[7px] text-[#E8E0D0]/20">{v.faction}</span>
                  </div>
                  <p className="font-['Outfit'] text-[#E8E0D0]/40 text-[9px]">{v.goal}</p>
                  <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[8px] italic">Night: {v.night}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Lobby view
  if (view === 'lobby') {
    return (
      <div className="space-y-4" data-testid="waterfront-table">
        <div className="flex items-center justify-between">
          <button onClick={onBack} className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px] tracking-widest hover:text-[#C9A84C]">BACK</button>
          <button onClick={() => setView('howto')} className="font-['Outfit'] text-[#9B59B6] text-[9px] tracking-widest hover:text-[#9B59B6]/80" data-testid="howto-btn">
            HOW TO PLAY
          </button>
        </div>
        <div className="text-center">
          <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#E8E0D0]" data-testid="table-title">THE WATERFRONT TABLE</h3>
          <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px] tracking-widest">SOCIAL DEDUCTION | 2-8 PLAYERS | $5,000 BUY-IN</p>
          <p className="font-['Outfit'] text-[#E8E0D0]/15 text-[8px] mt-1">Hidden roles. Night actions. Day votes. Trust no one.</p>
        </div>

        {msg && <p className="font-['Outfit'] text-[#8B1A1A] text-xs text-center">{msg}</p>}

        {/* Create table */}
        <div className="bg-[#111820] border border-[#C9A84C]/15 rounded-lg p-4 space-y-3">
          <p className="font-['Outfit'] text-[#C9A84C] text-[9px] tracking-widest">HOST A TABLE</p>
          <div className="flex items-center gap-2">
            <span className="font-['Outfit'] text-[#E8E0D0]/30 text-xs">Players:</span>
            {[2, 3, 4, 5, 6, 7, 8].map(n => (
              <button key={n} onClick={() => setMaxPlayers(n)}
                className={`w-7 h-7 rounded-lg font-['Outfit'] text-xs ${maxPlayers === n ? 'bg-[#C9A84C]/20 text-[#C9A84C] border border-[#C9A84C]/40' : 'bg-[#0a0c10] text-[#E8E0D0]/30 border border-[#1A2535]'}`}>
                {n}
              </button>
            ))}
          </div>
          <button onClick={createTable} disabled={loading}
            className="w-full py-2.5 bg-[#C9A84C]/10 border border-[#C9A84C]/40 rounded-lg text-[#C9A84C] font-['Outfit'] text-xs tracking-widest hover:bg-[#C9A84C]/20 disabled:opacity-30"
            data-testid="create-table-btn">
            {loading ? '...' : 'SIT DOWN ($5,000)'}
          </button>
        </div>

        {/* Open tables */}
        {openTables.length > 0 && (
          <div>
            <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[8px] tracking-widest mb-2">OPEN TABLES</p>
            <div className="space-y-1.5">
              {openTables.map(t => (
                <div key={t.table_id} className="flex items-center justify-between px-3 py-2 bg-[#111820] border border-[#1A2535] rounded-lg">
                  <div>
                    <span className="font-['Outfit'] text-[#E8E0D0] text-xs">{t.player_count}/{t.max_players} seated</span>
                    <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[8px]">{t.player_names?.join(', ')}</p>
                  </div>
                  <button onClick={() => joinTable(t.table_id)}
                    className="px-3 py-1.5 bg-[#C9A84C]/10 border border-[#C9A84C]/30 rounded text-[#C9A84C] font-['Outfit'] text-[8px] tracking-widest">
                    JOIN
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Role guide with goals */}
        <div>
          <p className="font-['Outfit'] text-[#E8E0D0]/15 text-[8px] tracking-widest mb-2">ROLES AT THE TABLE</p>
          <div className="grid grid-cols-2 gap-1.5">
            {Object.entries(ROLE_VISUALS).map(([k, v]) => (
              <div key={k} className="flex items-start gap-2 px-2 py-2 bg-[#0a0c10] rounded border border-[#1A2535]">
                <span style={{ color: v.color }} className="mt-0.5">{v.icon}</span>
                <div>
                  <p className="font-['Outfit'] text-[8px] font-bold" style={{ color: v.color }}>{v.name}</p>
                  <p className="font-['Outfit'] text-[7px] text-[#E8E0D0]/30 leading-relaxed">{v.goal}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Game view
  const ts = tableState;
  if (!ts) return <p className="text-[#E8E0D0]/20 text-xs text-center py-8">Loading...</p>;

  const myRole = ts.my_role;
  const roleVis = ROLE_VISUALS[myRole] || {};
  const alivePlayers = ts.players?.filter(p => p.alive) || [];
  const otherAlive = alivePlayers.filter(p => p.game_id !== gameId);

  return (
    <div className="space-y-3" data-testid="table-game">
      <div className="flex items-center justify-between">
        <button onClick={() => { setView('lobby'); setTableId(null); }}
          className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px] tracking-widest hover:text-[#C9A84C]">BACK</button>
        <span className={`font-['Outfit'] text-[9px] tracking-widest px-2 py-0.5 rounded ${
          ts.status === 'night' ? 'bg-[#1A1A3A] text-[#6666CC]' : ts.status === 'day' ? 'bg-[#3A3A1A] text-[#C9A84C]' : ts.status === 'ended' ? 'bg-[#1A1A1A] text-[#E8E0D0]/40' : 'bg-[#1A2535] text-[#E8E0D0]/30'
        }`}>
          {ts.status === 'night' ? 'NIGHT' : ts.status === 'day' ? 'DAY' : ts.status === 'ended' ? 'ENDED' : 'WAITING'} | ROUND {ts.round}/{ts.max_rounds}
        </span>
      </div>

      {/* My Role — with goal */}
      {myRole && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center px-4 py-3 rounded-lg border" style={{ backgroundColor: roleVis.color + '08', borderColor: roleVis.color + '30' }}>
          <span style={{ color: roleVis.color }}>{roleVis.icon}</span>
          <p className="font-['Cormorant_Garamond'] text-lg font-bold mt-1" style={{ color: roleVis.color }}>{roleVis.name}</p>
          <p className="font-['Outfit'] text-[9px] text-[#E8E0D0]/40 mt-0.5">{roleVis.goal}</p>
          {ts.status === 'night' && <p className="font-['Outfit'] text-[8px] mt-1 italic" style={{ color: roleVis.color + '80' }}>Night: {roleVis.night}</p>}
        </motion.div>
      )}

      {msg && <p className="font-['Outfit'] text-[#C9A84C] text-xs text-center italic">"{msg}"</p>}

      {/* Waiting — start button + AI fill */}
      {ts.status === 'waiting' && (
        <div className="space-y-2">
          <button onClick={async () => {
            await fetch(`${API}/api/table/fill-ai/${tableId}`, { method: 'POST' });
            loadStatus();
          }}
            className="w-full py-2 bg-[#9B59B6]/10 border border-[#9B59B6]/30 rounded-lg text-[#9B59B6] font-['Outfit'] text-[9px] tracking-widest hover:bg-[#9B59B6]/20"
            data-testid="fill-ai-btn">
            FILL WITH AI PLAYERS
          </button>
          <button onClick={startGame}
            disabled={ts.players?.length < 2}
            className="w-full py-3 bg-[#C9A84C]/10 border border-[#C9A84C]/40 rounded-lg text-[#C9A84C] font-['Outfit'] text-xs tracking-widest disabled:opacity-30"
            data-testid="start-table-btn">
            START GAME ({ts.players?.length} PLAYERS)
          </button>
        </div>
      )}

      {/* Night Phase — Actions */}
      {ts.status === 'night' && ts.my_actions?.length > 0 && (
        <div className="space-y-2">
          <p className="font-['Outfit'] text-[#6666CC] text-[9px] tracking-widest">NIGHT ACTION</p>
          {ts.my_actions.some(a => ['investigate', 'eliminate_player', 'protect_player', 'sabotage'].includes(a)) && (
            <div className="space-y-1">
              <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[8px]">Select target:</p>
              {otherAlive.map(p => (
                <button key={p.game_id} onClick={() => setSelectedTarget(p.game_id)}
                  className={`w-full px-3 py-2 rounded-lg border text-left font-['Outfit'] text-xs ${
                    selectedTarget === p.game_id ? 'bg-[#C9A84C]/10 border-[#C9A84C]/40 text-[#C9A84C]' : 'bg-[#111820] border-[#1A2535] text-[#E8E0D0]/60'
                  }`}>
                  {p.username} {p.exposed && p.role ? `(${p.role})` : ''}
                </button>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            {ts.my_actions.map(a => (
              <button key={a} onClick={() => submitNightAction(a)}
                className="flex-1 py-2 bg-[#1A1A3A] border border-[#6666CC]/30 rounded-lg text-[#6666CC] font-['Outfit'] text-[9px] tracking-widest hover:bg-[#6666CC]/10"
                data-testid={`night-action-${a}`}>
                {a.replace(/_/g, ' ').toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Day Phase — Vote */}
      {ts.status === 'day' && (
        <div className="space-y-2">
          <p className="font-['Outfit'] text-[#C9A84C] text-[9px] tracking-widest">DAY VOTE — Who gets eliminated?</p>
          {otherAlive.length === 0 ? (
            <p className="font-['Outfit'] text-[#E8E0D0]/20 text-xs text-center py-4">No targets available.</p>
          ) : (
            <div className="space-y-1">
              {otherAlive.map(p => (
                <button key={p.game_id}
                  onClick={() => { if (!selectedTarget) { setSelectedTarget(p.game_id); submitVote(p.game_id); } }}
                  disabled={!!selectedTarget}
                  className={`w-full px-3 py-2.5 bg-[#111820] border rounded-lg text-left font-['Outfit'] text-xs transition-all ${
                    selectedTarget === p.game_id
                      ? 'border-[#8B1A1A] text-[#8B1A1A] bg-[#8B1A1A]/10'
                      : selectedTarget
                        ? 'border-[#1A2535] text-[#E8E0D0]/20 opacity-40'
                        : 'border-[#1A2535] text-[#E8E0D0]/60 hover:border-[#8B1A1A]/40 hover:text-[#8B1A1A]'
                  }`}
                  data-testid={`vote-${p.game_id}`}>
                  {selectedTarget === p.game_id ? 'VOTED ' : ''}{p.username} {p.exposed && p.role ? `(${p.role.toUpperCase()})` : ''} {p.income_earned > 0 ? `— $${p.income_earned.toLocaleString()}` : ''}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Game Ended */}
      {ts.status === 'ended' && (
        <div className="text-center py-4">
          <p className="font-['Cormorant_Garamond'] text-xl font-bold text-[#C9A84C]">
            {ts.winner_faction === 'cmd' ? 'CMD WINS' : 'THE LAW WINS'}
          </p>
          <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px]">The waterfront has spoken.</p>
        </div>
      )}

      {/* Players list */}
      <div>
        <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[8px] tracking-widest mb-1">PLAYERS</p>
        <div className="space-y-1">
          {ts.players?.map((p, i) => {
            const rv = ROLE_VISUALS[p.role] || {};
            return (
              <div key={i} className={`flex items-center justify-between px-3 py-1.5 rounded-lg border ${
                !p.alive ? 'bg-[#0a0c10] border-[#1A2535] opacity-40' : p.game_id === gameId ? 'bg-[#C9A84C]/5 border-[#C9A84C]/15' : 'bg-[#111820] border-[#1A2535]'
              }`}>
                <div className="flex items-center gap-2">
                  {p.role && (ts.status === 'ended' || p.exposed || p.game_id === gameId) && (
                    <span style={{ color: rv.color }}>{rv.icon}</span>
                  )}
                  <span className={`font-['Outfit'] text-xs ${!p.alive ? 'line-through text-[#E8E0D0]/20' : 'text-[#E8E0D0]'}`}>{p.username}</span>
                </div>
                <div className="flex items-center gap-2">
                  {p.voted_out && <span className="font-['Outfit'] text-[7px] text-[#8B1A1A]">VOTED OUT</span>}
                  {!p.alive && !p.voted_out && <span className="font-['Outfit'] text-[7px] text-[#8B1A1A]">ELIMINATED</span>}
                  {p.role && (ts.status === 'ended' || p.exposed) && (
                    <span className="font-['Outfit'] text-[7px]" style={{ color: rv.color }}>{p.role?.toUpperCase()}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Night/Day log */}
      {(ts.night_log?.length > 0 || ts.day_log?.length > 0) && (
        <div>
          <p className="font-['Outfit'] text-[#E8E0D0]/15 text-[7px] tracking-widest mb-1">LOG</p>
          <div className="space-y-0.5">
            {[...(ts.day_log || []), ...(ts.night_log || [])].slice(-5).map((log, i) => (
              <p key={i} className="font-['Outfit'] text-[#E8E0D0]/25 text-[9px] italic">"{log}"</p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================
// 1v1 CHALLENGE UI
// =============================================
const ChallengePanel = ({ gameId, onBack }) => {
  const [challenges, setChallenges] = useState({ incoming: [], outgoing: [], active: [] });
  const [targetId, setTargetId] = useState('');
  const [stakeAmount, setStakeAmount] = useState('5000');
  const [activeChallenge, setActiveChallenge] = useState(null);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    const res = await fetch(`${API}/api/challenge/pending/${gameId}`);
    if (res.ok) setChallenges(await res.json());
  }, [gameId]);

  useEffect(() => { load(); const i = setInterval(load, 5000); return () => clearInterval(i); }, [load]);

  const createChallenge = async () => {
    const res = await fetch(`${API}/api/challenge/create`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ game_id: gameId, target_game_id: targetId, stake_type: 'cash', stake_amount: parseInt(stakeAmount) }),
    });
    const d = await res.json();
    setMsg(res.ok ? d.narrator : d.detail);
    if (res.ok) { setTargetId(''); load(); }
  };

  const acceptChallenge = async (chId) => {
    const res = await fetch(`${API}/api/challenge/accept/${chId}?game_id=${gameId}`, { method: 'POST' });
    const d = await res.json();
    setMsg(res.ok ? d.narrator : d.detail);
    load();
  };

  const submitMove = async (chId, move) => {
    const res = await fetch(`${API}/api/challenge/move`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ game_id: gameId, challenge_id: chId, move }),
    });
    const d = await res.json();
    setMsg(d.narrator);
    if (d.match_over) setActiveChallenge(null);
    load();
  };

  // Active challenge view
  if (activeChallenge) {
    const ch = activeChallenge;
    const isChallenger = ch.challenger_id === gameId;
    return (
      <div className="space-y-3">
        <button onClick={() => setActiveChallenge(null)} className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px] tracking-widest">BACK</button>
        <div className="text-center">
          <p className="font-['Outfit'] text-[#E8E0D0]/40 text-[9px]">{ch.challenger_name} vs {ch.defender_name}</p>
          <p className="font-['Outfit'] text-[#C9A84C] text-xs font-bold">${ch.stake_amount?.toLocaleString()} ON THE LINE</p>
          <p className="font-['Outfit'] text-[#E8E0D0] text-sm mt-1">{ch.challenger_wins} — {ch.defender_wins}</p>
        </div>
        {msg && <p className="font-['Outfit'] text-[#C9A84C] text-xs text-center italic">"{msg}"</p>}
        <div className="grid grid-cols-3 gap-2">
          {['attack', 'defend', 'bluff'].map(m => (
            <button key={m} onClick={() => submitMove(ch.challenge_id, m)}
              className="py-3 bg-[#111820] border border-[#1A2535] rounded-lg text-[#E8E0D0] font-['Outfit'] text-xs tracking-widest hover:border-[#C9A84C]/40 hover:text-[#C9A84C]"
              data-testid={`move-${m}`}>
              {m === 'attack' ? <Sword size={16} className="mx-auto mb-1" /> : m === 'defend' ? <Shield size={16} className="mx-auto mb-1" /> : <Eye size={16} className="mx-auto mb-1" />}
              {m.toUpperCase()}
            </button>
          ))}
        </div>
        <p className="font-['Outfit'] text-[#E8E0D0]/15 text-[8px] text-center">Attack beats Bluff. Defend beats Attack. Bluff beats Defend.</p>

        {/* Round history */}
        {ch.rounds?.length > 0 && (
          <div className="space-y-1">
            {ch.rounds.map((r, i) => (
              <div key={i} className="flex items-center justify-between px-3 py-1.5 bg-[#0a0c10] rounded text-[9px] font-['Outfit']">
                <span className="text-[#E8E0D0]/30">Round {i + 1}</span>
                <span className="text-[#E8E0D0]/50">{r.challenger_move} vs {r.defender_move}</span>
                <span className={r.winner === 'challenger' ? 'text-[#C9A84C]' : r.winner === 'defender' ? 'text-[#8B1A1A]' : 'text-[#E8E0D0]/20'}>
                  {r.winner === 'draw' ? 'DRAW' : r.winner === 'challenger' ? ch.challenger_name : ch.defender_name}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="challenge-panel">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px] tracking-widest hover:text-[#C9A84C]">BACK</button>
      </div>
      <div className="text-center">
        <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#E8E0D0]" data-testid="challenge-title">1v1 DIRECT CHALLENGE</h3>
        <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px] tracking-widest">ATTACK / DEFEND / BLUFF — BEST OF 3</p>
      </div>

      {msg && <p className="font-['Outfit'] text-[#C9A84C] text-xs text-center italic">"{msg}"</p>}

      {/* Challenge someone */}
      <div className="space-y-2">
        <p className="font-['Outfit'] text-[#C9A84C] text-[9px] tracking-widest">SEND CHALLENGE</p>
        <input type="text" value={targetId} onChange={e => setTargetId(e.target.value)} placeholder="Opponent Game ID"
          className="w-full px-3 py-2 bg-[#0a0c10] border border-[#1A2535] rounded-lg text-[#E8E0D0] font-['Outfit'] text-xs outline-none" />
        <div className="flex gap-2">
          <input type="number" value={stakeAmount} onChange={e => setStakeAmount(e.target.value)} placeholder="Cash stake"
            className="flex-1 px-3 py-2 bg-[#0a0c10] border border-[#1A2535] rounded-lg text-[#E8E0D0] font-['Outfit'] text-xs outline-none" />
          <button onClick={createChallenge} disabled={!targetId}
            className="px-4 py-2 bg-[#8B1A1A]/10 border border-[#8B1A1A]/30 rounded-lg text-[#8B1A1A] font-['Outfit'] text-[9px] tracking-widest disabled:opacity-30"
            data-testid="send-challenge-btn">
            CHALLENGE
          </button>
        </div>
      </div>

      {/* Incoming */}
      {challenges.incoming?.length > 0 && (
        <div>
          <p className="font-['Outfit'] text-[#8B1A1A]/50 text-[8px] tracking-widest mb-1">INCOMING CHALLENGES</p>
          {challenges.incoming.map(ch => (
            <div key={ch.challenge_id} className="flex items-center justify-between px-3 py-2 bg-[#111820] border border-[#8B1A1A]/15 rounded-lg mb-1">
              <div>
                <span className="font-['Outfit'] text-xs text-[#E8E0D0]">{ch.challenger_name}</span>
                <p className="font-['Outfit'] text-[8px] text-[#4ADE80]">${ch.stake_amount?.toLocaleString()} stake</p>
              </div>
              <button onClick={() => acceptChallenge(ch.challenge_id)}
                className="px-3 py-1 bg-[#C9A84C]/10 border border-[#C9A84C]/30 rounded text-[#C9A84C] font-['Outfit'] text-[8px] tracking-widest">
                ACCEPT
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Active */}
      {challenges.active?.length > 0 && (
        <div>
          <p className="font-['Outfit'] text-[#C9A84C]/50 text-[8px] tracking-widest mb-1">ACTIVE</p>
          {challenges.active.map(ch => (
            <button key={ch.challenge_id} onClick={() => setActiveChallenge(ch)}
              className="w-full flex items-center justify-between px-3 py-2 bg-[#111820] border border-[#C9A84C]/15 rounded-lg mb-1 text-left">
              <span className="font-['Outfit'] text-xs text-[#E8E0D0]">
                {ch.challenger_id === gameId ? `vs ${ch.defender_name}` : `vs ${ch.challenger_name}`}
              </span>
              <span className="font-['Outfit'] text-[9px] text-[#C9A84C]">{ch.status.toUpperCase()}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// =============================================
// PLAYER PROFILE UI
// =============================================
const BADGE_ICONS = {
  crown: <Crown size={14} />, sword: <Sword size={14} />, shield: <Shield size={14} />,
  map_pin: <MapPin size={14} />, users: <Users size={14} />, hash: <Hash size={14} />,
  star: <Star size={14} />, fire: <Flame size={14} />, target: <Target size={14} />,
  book: <BookOpen size={14} />, dollar: <DollarSign size={14} />,
};

const PlayerProfile = ({ gameId, onBack }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/profile/${gameId}`).then(r => r.json()).then(setProfile).catch(() => {}).finally(() => setLoading(false));
  }, [gameId]);

  if (loading) return <p className="font-['Outfit'] text-[#E8E0D0]/20 text-xs text-center py-8">Loading...</p>;
  if (!profile) return null;

  return (
    <div className="space-y-3" data-testid="player-profile">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px] tracking-widest hover:text-[#C9A84C]">BACK</button>
      </div>

      {/* Header */}
      <div className="text-center">
        <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#E8E0D0]">{profile.username}</h3>
        <p className="font-['Outfit'] text-[#C9A84C] text-xs">{profile.title} — LEVEL {profile.level}</p>
        <div className="flex items-center justify-center gap-2 mt-1">
          <span className="font-['Outfit'] text-[8px] px-2 py-0.5 bg-[#C9A84C]/10 border border-[#C9A84C]/20 rounded text-[#C9A84C]">{profile.faction?.toUpperCase()}</span>
          <span className="font-['Outfit'] text-[8px] text-[#E8E0D0]/20">Era {profile.current_era}</span>
          {profile.prestige_cycle > 0 && <span className="font-['Outfit'] text-[8px] text-[#FFD700]">P{profile.prestige_cycle}</span>}
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-4 gap-1.5">
        {[
          { label: 'CASH', value: `$${profile.cash?.toLocaleString()}`, color: '#4ADE80' },
          { label: 'CRYSTALS', value: profile.water_crystals, color: '#4A9BD9' },
          { label: 'RESPECT', value: profile.respect, color: '#C9A84C' },
          { label: 'STREAK', value: `${profile.check_in_streak}d`, color: '#E74C3C' },
        ].map((s, i) => (
          <div key={i} className="bg-[#111820] rounded-lg p-2 text-center">
            <p className="font-['Outfit'] text-[6px] text-[#E8E0D0]/20 tracking-widest">{s.label}</p>
            <p className="font-['Outfit'] text-xs font-bold" style={{ color: s.color }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Territory & Combat */}
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-[#111820] border border-[#C9A84C]/10 rounded-lg p-3">
          <p className="font-['Outfit'] text-[7px] text-[#E8E0D0]/20 tracking-widest mb-1">TERRITORY</p>
          <p className="font-['Outfit'] text-lg font-bold text-[#C9A84C]">{profile.territories_owned}</p>
          <p className="font-['Outfit'] text-[9px] text-[#4ADE80]">${profile.total_income?.toLocaleString()}/cycle</p>
        </div>
        <div className="bg-[#111820] border border-[#8B1A1A]/10 rounded-lg p-3">
          <p className="font-['Outfit'] text-[7px] text-[#E8E0D0]/20 tracking-widest mb-1">COMBAT</p>
          <p className="font-['Outfit'] text-xs text-[#E8E0D0]">
            ATK <span className="text-[#4ADE80]">{profile.attack_wins}W</span>/<span className="text-[#8B1A1A]">{profile.attack_losses}L</span>
          </p>
          <p className="font-['Outfit'] text-xs text-[#E8E0D0]">
            DEF <span className="text-[#4ADE80]">{profile.defense_wins}W</span>/<span className="text-[#8B1A1A]">{profile.defense_losses}L</span>
          </p>
          <p className="font-['Outfit'] text-[8px] text-[#E8E0D0]/30 mt-1">
            1v1: {profile.challenge_wins}W/{profile.challenge_total}T | Table: {profile.table_games}
          </p>
        </div>
      </div>

      {/* Crew */}
      {profile.crew && (
        <div className="bg-[#111820] border border-[#C9A84C]/15 rounded-lg px-3 py-2">
          <div className="flex items-center justify-between">
            <span className="font-['Outfit'] text-xs text-[#C9A84C] font-bold">[{profile.crew.tag}] {profile.crew.name}</span>
            <span className="font-['Outfit'] text-[8px] text-[#FFD700]">{profile.crew.rank?.toUpperCase()}</span>
          </div>
        </div>
      )}

      {/* Badges */}
      <div>
        <p className="font-['Outfit'] text-[8px] text-[#E8E0D0]/20 tracking-widest mb-1.5">BADGES ({profile.badge_count})</p>
        {profile.badges?.length > 0 ? (
          <div className="grid grid-cols-2 gap-1.5">
            {profile.badges.map((b, i) => (
              <div key={i} className="flex items-center gap-2 px-2 py-1.5 bg-[#0a0c10] rounded-lg border border-[#1A2535]">
                <span style={{ color: b.color }}>{BADGE_ICONS[b.icon] || <Star size={14} />}</span>
                <div>
                  <p className="font-['Outfit'] text-[8px] font-bold" style={{ color: b.color }}>{b.name}</p>
                  <p className="font-['Outfit'] text-[6px] text-[#E8E0D0]/20">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="font-['Outfit'] text-[#E8E0D0]/15 text-xs text-center py-2">No badges yet. Keep playing.</p>
        )}
      </div>

      {/* Featured territories */}
      {profile.featured_territories?.length > 0 && (
        <div>
          <p className="font-['Outfit'] text-[8px] text-[#E8E0D0]/20 tracking-widest mb-1">OWNED TERRITORY</p>
          <div className="space-y-1">
            {profile.featured_territories.map((t, i) => (
              <div key={i} className="flex items-center justify-between px-2 py-1 bg-[#0a0c10] rounded border border-[#1A2535]">
                <span className="font-['Outfit'] text-[9px] text-[#C9A84C]">{t.kow_name}</span>
                <span className="font-['Outfit'] text-[8px] text-[#4ADE80]">${t.income_per_cycle?.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export { WaterfrontTable, ChallengePanel, PlayerProfile };
